"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Calendar, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import type { BlogPost } from "@/lib/types";
import { displayCategory } from "@/lib/category-display";

interface BlogCardProps {
  post: BlogPost;
  horizontal?: boolean;
}

export function BlogCard({ post, horizontal = false }: BlogCardProps) {
  const [imgSrc, setImgSrc] = useState(post.coverImage || "");
  const [hasError, setHasError] = useState(false);

  if (!post || !post.slug || !post.title) {
    return null;
  }

  const handleImageError = () => {
    if (!hasError) {
      setImgSrc("/placeholder-blog.webp");
      setHasError(true);
    }
  };

  const formattedDate = new Date(post.publishedAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  if (horizontal) {
    return (
      <Link href={`/blog/${post.slug}`}>
        <Card className="hover:shadow-md transition-shadow group border-border p-0 overflow-hidden">
          <div className="flex flex-col sm:flex-row">
            <div className="relative sm:w-56 lg:w-64 h-44 sm:h-auto flex-shrink-0 bg-gradient-to-br from-muted to-background overflow-hidden">
              {post.coverImage && (
                <Image
                  src={imgSrc}
                  alt={post.title}
                  fill
                  sizes="(max-width: 640px) 90vw, 256px"
                  className="object-cover"
                  onError={handleImageError}
                />
              )}
            </div>
            <CardContent className="p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-medium text-accent bg-accent/10 px-2 py-1 rounded">
                  {displayCategory(post.category)}
                </span>
              </div>

              <div className="flex items-start justify-between gap-4">
                <h3 className="font-semibold text-xl text-foreground group-hover:text-accent transition-colors line-clamp-2 leading-tight">
                  {post.title}
                </h3>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
              </div>

              <p className="text-base text-muted-foreground mt-2 line-clamp-3 leading-relaxed">
                {post.description}
              </p>

              <div className="mt-auto pt-4 flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formattedDate}
                </span>
              </div>

              {post.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {post.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="text-sm bg-muted text-muted-foreground px-2 py-0.5 rounded"
                    >
                      {displayCategory(tag)}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </div>
        </Card>
      </Link>
    );
  }

  return (
    <Link href={`/blog/${post.slug}`}>
      <Card className="h-full hover:shadow-md transition-shadow group border-border p-0">
        <div className="relative h-48 w-full bg-gradient-to-br from-muted to-background overflow-hidden rounded-t-lg">
          {post.coverImage && (
            <Image
              src={imgSrc}
              alt={post.title}
              fill
              sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 33vw"
              className="object-cover"
              onError={handleImageError}
            />
          )}
        </div>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="text-xs font-medium text-accent bg-accent/10 px-2 py-1 rounded"
            >
              {displayCategory(post.category)}
            </span>
          </div>

          <div className="flex items-start justify-between gap-4">
            <h3 className="font-semibold text-lg text-foreground group-hover:text-accent transition-colors line-clamp-2 leading-tight">
              {post.title}
            </h3>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
          </div>

          <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">
            {post.description}
          </p>

          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {post.author}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formattedDate}
            </span>
          </div>

          {post.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded"
                >
                  {displayCategory(tag)}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
